/**
 * A single line of a stock operation. Transfers use both location fields,
 * every other operation uses warehouseLocationId only. Adjustments set direction.
 */
export class StockOperationLine {
  productId: number = -1;
  warehouseLocationId: number = -1;
  toWarehouseLocationId: number = -1;
  direction: string = 'in';
  qty: number = 0;

  /**
   * The batch number printed on these goods, or null where they carry none — which is the normal
   * case and is what this field is initialised to.
   *
   * A string, because a batch number "might not be a number": there is no batch table and no id to
   * resolve, so this is the number itself as the operator read it off the pallet. `A1` and
   * `LOT-2026-04` are ordinary values here.
   *
   * `null` and NOT the `-1` this file uses for every other unpicked dropdown, and that is a
   * correctness point rather than a style choice. The backend reads a batch off a line with
   * `batchNumberOf`, which treats undefined, null and '' as "no batch" and normalises anything else
   * into stored form — so a `-1` here would be recorded as a batch literally numbered "-1" on every
   * un-batched line, which is worse than being refused because it would be kept.
   *
   * Batch is line-level and never operation-level: a batch belongs to one product, so an operation
   * covering several products cannot share one, and the backend refuses a `batchNumber` on the
   * operation body outright rather than spreading it across the lines.
   */
  batchNumber: string = null;

  /**
   * What these goods weighed before the movement and again after it, or null where they were not
   * weighed — which is the normal case and is what both fields are initialised to.
   *
   * Net moved is `endWeight - startWeight` and is never posted: the backend derives it, so a third
   * field here would be a second opinion about a subtraction.
   *
   * Independently optional. A weighbridge can weigh goods in and never get a second reading, so a
   * lone startWeight is a measurement that was actually taken rather than a half-filled pair.
   *
   * `null` and NOT the `-1` this file uses for every other unpicked dropdown, and NOT `0`. Both are
   * correctness points rather than style choices. The backend reads a weight off a line with
   * `weightOrNull`, which treats undefined, null and '' as "not weighed" and keeps anything else it
   * can use — so a `-1` here would be refused as a negative reading, and a `0` would be recorded as
   * a real reading of nothing on every unweighed line, which is worse than being refused because it
   * would be kept. Zero has to stay a value an operator can mean: an empty container on a tared
   * scale genuinely weighs nothing.
   *
   * Weight is line-level and never operation-level: one pair of scale readings cannot describe
   * several lines, and the backend refuses a weight on the operation body outright rather than
   * spreading it across them.
   */
  startWeight: number = null;
  endWeight: number = null;
}

/**
 * Posted as a single request per operation. The backend runs the whole batch
 * in one transaction, so lines are never posted individually.
 */
export class StockOperation {
  referenceId: number = -1;
  lines: StockOperationLine[] = [];
}
